import styles from "../styles.module.scss";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Stack } from "@altimateai/lego";
import { getQueryTextAndIndicesByHash } from "@/helpers/apis";
import { ColumnDatatype } from "@/components/column";
import { NodeIcon, ViewsCodeModalArgs, ViewsTypeBadge } from "@altimateai/ui-components/lineage";
import classNames from "classnames";
import { Accordion, Card as Card2, Typography } from "@altimateai/lego";
import { Paginate } from "@/components/paginate";
import usePaginate from "@/components/paginate/usePaginate";
import { QueryTextIndicesResponse } from "@/pages/query/types";
import { QueryTextItem } from "./QueryTextItem";
import { ComponentLoader } from "@/components/loader";

interface Props {
  viewsCodeArgs: ViewsCodeModalArgs & { label: string };
  disableQueryTextView?: boolean;
}

export const ViewsCodeSideModal = ({ viewsCodeArgs, disableQueryTextView = false }: Props) => {
  const table = viewsCodeArgs.label;
  const [openAccordion, setOpenAccordion] = useState("");
  const { page, pageSize, setPage, updatePageSize } = usePaginate();
  const flattenList = useMemo(() => {
    const queryHashMap: Record<string, string[]> = {};
    Object.values(viewsCodeArgs.viewsCode)
      .flat()
      .forEach(([code, query_hash]) => {
        queryHashMap[query_hash] = queryHashMap[query_hash] || [];
        queryHashMap[query_hash].push(code);
      });
    return Object.entries(queryHashMap).sort((a, b) => a[0].localeCompare(b[0]));
  }, [viewsCodeArgs.viewsCode]);

  const pageItemList = flattenList.slice(page * pageSize, (page + 1) * pageSize);

  const { data, isLoading } = useQuery({
    queryKey: ["GET-QUERY-TEXT-SPAN", page, pageSize, flattenList],
    queryFn: async () => {
      const queryTextMap: Record<string, QueryTextIndicesResponse | undefined> = {};
      const result = await Promise.all(
        pageItemList.map(([query_hash, spans]) =>
          getQueryTextAndIndicesByHash(query_hash, { spans })
        )
      );
      result.forEach((resp, i) => {
        queryTextMap[pageItemList[i][0]] = resp;
      });
      return queryTextMap;
    },
    enabled: !disableQueryTextView,
    gcTime: 5 * 60 * 1000,
  });

  return (
    <Stack direction="column">
      <Stack align="center">
        <ColumnDatatype datatype={viewsCodeArgs.datatype || ""} color={undefined} />
        <div className="al-text-dark-gray fs-lg">{viewsCodeArgs.column}</div>
      </Stack>
      <Card2 className="al-bg-gray-100 al-p-2">
        <Stack direction="column">
          <div className={styles.header_section_row}>
            <Typography variant="section-heading" className="al-mb-0 al-text-dark-gray">
              Table
            </Typography>
            <div className={styles.model_views_type}>
              <div style={{ width: "20px", height: "20px" }}>
                <NodeIcon nodeType={viewsCodeArgs.nodeType} />
              </div>
              {table}
            </div>
          </div>
          <div className={styles.header_section_row}>
            <Typography variant="section-heading" className="al-mb-0 al-text-dark-gray">
              Type
            </Typography>
            <div className={styles.model_views_type}>
              <ViewsTypeBadge viewsType={viewsCodeArgs.viewsType} />
              {viewsCodeArgs.viewsType}
            </div>
          </div>
          <div className={styles.header_section_row}>
            <Typography variant="section-heading" className="al-mb-0 al-text-dark-gray">
              FQN
            </Typography>
            <div className={styles.model_views_type}>
              {viewsCodeArgs.table.split("://")[1]}/{viewsCodeArgs.column}
            </div>
          </div>
        </Stack>
      </Card2>
      <>
        <Typography variant="section-heading" className="al-mb-0">
          Transformations
        </Typography>
        {isLoading ? (
          <ComponentLoader label="Loading..." />
        ) : (
          <Stack direction="column">
            <div>
              <div className={styles.table}>
                <div className={classNames(styles.table_row, styles.table_header)}>
                  <div>Queries</div>
                  <div />
                </div>
                <Accordion
                  type="single"
                  defaultValue={undefined}
                  collapsible
                  onValueChange={setOpenAccordion}
                >
                  {pageItemList.map(([query_hash, spans]) => (
                    <QueryTextItem
                      key={query_hash}
                      query_hash={query_hash}
                      spans={spans}
                      query_response={data?.[query_hash]}
                      openAccordion={openAccordion}
                    />
                  ))}
                </Accordion>
              </div>
              <Paginate
                itemCount={flattenList.length}
                page={page}
                pageSize={pageSize}
                numPages={Math.ceil(flattenList.length / pageSize)}
                onPageClick={setPage}
                onPageSizeChange={updatePageSize}
              />
            </div>
          </Stack>
        )}
      </>
    </Stack>
  );
};
